/**
 * useWallet — Phantom (Solana) + MetaMask (EVM) wallet connection hook.
 * Supports connect/disconnect, balance display, and transaction signing.
 */

import { useCallback } from 'react';
import { useTerminalStore } from '../stores/terminal.store.js';
import { getHLAccountEquity } from '../services/hyperliquidOrderService.js';
import type { WalletInfo } from '@arc/shared';
import type { Transaction, VersionedTransaction } from '@solana/web3.js';

// ─── Window extension types ────────────────────────────────────────────────────

interface PhantomProvider {
  isPhantom:         boolean;
  publicKey:         { toBase58: () => string } | null;
  connect:           (opts?: { onlyIfTrusted?: boolean }) => Promise<{ publicKey: { toBase58: () => string } }>;
  disconnect:        () => Promise<void>;
  on:                (event: string, cb: () => void) => void;
  off:               (event: string, cb: () => void) => void;
  // Signing (needed for Jupiter swaps)
  signTransaction:     <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(txs: T[]) => Promise<T[]>;
}

interface EthereumProvider {
  isMetaMask?: boolean;
  request:     <T = unknown>(args: { method: string; params?: unknown[] }) => Promise<T>;
  on:          (event: string, cb: (...args: unknown[]) => void) => void;
  removeListener: (event: string, cb: (...args: unknown[]) => void) => void;
}

declare global {
  interface Window {
    solana?:   PhantomProvider;
    ethereum?: EthereumProvider;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function shortenAddress(addr: string): string {
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useWallet() {
  const connectWallet    = useTerminalStore(s => s.connectWallet);
  const disconnectWallet = useTerminalStore(s => s.disconnectWallet);
  const wallets          = useTerminalStore(s => s.wallets);
  const activeWallet     = useTerminalStore(s => s.activeWallet);
  const setLiveAccount   = useTerminalStore(s => s.setLiveAccount);

  // ── Phantom ────────────────────────────────────────────────────────────────

  const connectPhantom = useCallback(async () => {
    const provider = window.solana;

    if (!provider?.isPhantom) {
      window.open('https://phantom.app/', '_blank');
      return;
    }

    try {
      const resp    = await provider.connect();
      const address = resp.publicKey.toBase58();

      // Try to get SOL balance
      let balance: number | undefined;
      try {
        const res = await fetch('https://api.mainnet-beta.solana.com', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            jsonrpc: '2.0', id: 1,
            method:  'getBalance',
            params:  [address],
          }),
        });
        const data = await res.json() as { result?: { value?: number } };
        balance = (data?.result?.value ?? 0) / 1e9;
      } catch { /* ignore */ }

      const wallet: WalletInfo = {
        type:    'phantom',
        address,
        chain:   'solana',
        ...(balance !== undefined ? { balance } : {}),
      };

      connectWallet(wallet);
    } catch (err) {
      console.error('[Phantom] Connection failed:', err);
    }
  }, [connectWallet]);

  const disconnectPhantom = useCallback(async () => {
    try { await window.solana?.disconnect(); } catch { /* ignore */ }
    const phantom = wallets.find(w => w.type === 'phantom');
    if (phantom) disconnectWallet(phantom.address);
    setLiveAccount(null);
  }, [wallets, disconnectWallet, setLiveAccount]);

  // ── MetaMask ───────────────────────────────────────────────────────────────

  const connectMetaMask = useCallback(async () => {
    const provider = window.ethereum;

    if (!provider?.isMetaMask) {
      window.open('https://metamask.io/', '_blank');
      return;
    }

    try {
      const accounts = await provider.request<string[]>({ method: 'eth_requestAccounts' });
      const address  = accounts[0];
      if (!address) return;

      // Get ETH balance
      let balance: number | undefined;
      try {
        const hex = await provider.request<string>({
          method: 'eth_getBalance',
          params: [address, 'latest'],
        });
        balance = parseInt(hex, 16) / 1e18;
      } catch { /* ignore */ }

      // Get Hyperliquid account equity
      let usdBalance: number | undefined;
      try {
        const { equity } = await getHLAccountEquity(address);
        if (equity > 0) usdBalance = equity;
      } catch { /* ignore */ }

      const wallet: WalletInfo = {
        type:  'metamask',
        address,
        chain: 'ethereum',
        ...(balance    !== undefined ? { balance }    : {}),
        ...(usdBalance !== undefined ? { usdBalance } : {}),
      };

      connectWallet(wallet);

      // Update live account in store if equity found
      if (usdBalance !== undefined) {
        setLiveAccount({ equity: usdBalance, available: usdBalance, positions: 0 });
      }
    } catch (err) {
      console.error('[MetaMask] Connection failed:', err);
    }
  }, [connectWallet, setLiveAccount]);

  const disconnectMetaMask = useCallback(() => {
    const mm = wallets.find(w => w.type === 'metamask');
    if (mm) disconnectWallet(mm.address);
    setLiveAccount(null);
  }, [wallets, disconnectWallet, setLiveAccount]);

  // ── Balance refresh ────────────────────────────────────────────────────────

  const refreshWalletBalance = useCallback(async () => {
    const wallet = activeWallet;
    if (!wallet) return;

    try {
      if (wallet.type === 'metamask') {
        const { equity, available } = await getHLAccountEquity(wallet.address);
        setLiveAccount({ equity, available, positions: 0 });
        connectWallet({ ...wallet, usdBalance: equity });
      } else if (wallet.type === 'phantom') {
        const res = await fetch('https://api.mainnet-beta.solana.com', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            jsonrpc: '2.0', id: 1,
            method:  'getBalance',
            params:  [wallet.address],
          }),
        });
        const data = await res.json() as { result?: { value?: number } };
        const balance = (data?.result?.value ?? 0) / 1e9;
        connectWallet({ ...wallet, balance });
      }
    } catch { /* ignore */ }
  }, [activeWallet, connectWallet, setLiveAccount]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const phantomWallet  = wallets.find(w => w.type === 'phantom');
  const metaMaskWallet = wallets.find(w => w.type === 'metamask');

  return {
    wallets,
    activeWallet,
    phantomWallet,
    metaMaskWallet,
    isPhantomAvailable:   typeof window !== 'undefined' && !!window.solana?.isPhantom,
    isMetaMaskAvailable:  typeof window !== 'undefined' && !!window.ethereum?.isMetaMask,
    connectPhantom,
    connectMetaMask,
    disconnectPhantom,
    disconnectMetaMask,
    refreshWalletBalance,
    shortenAddress,
  };
}
